/**
 * Direcciones por idioma.
 *
 * El español vive en la raíz del dominio —así conserva las direcciones ya
 * posicionadas y las campañas— y cada uno de los otros idiomas tiene su propio
 * prefijo. Este módulo es la única fuente de verdad de esa correspondencia.
 */

export const LANG_CODES = ['ES', 'CA', 'EN', 'FR'];

export const LANG_PREFIXES = { ES: '', CA: '/ca', EN: '/en', FR: '/fr' };

export const OG_LOCALES = { ES: 'es_ES', CA: 'ca_ES', EN: 'en_GB', FR: 'fr_FR' };

// Páginas que existen en los cuatro idiomas. Debe mantenerse en sintonía con el
// listado de rutas de la app: lo que no figure aquí se sirve solo en español
// (acceso, registro y páginas de recuperación de contraseña).
export const LOCALIZED_PATHS = [
  '/',
  '/casos-exito',
  '/sobre-nosotros',
  '/contacto',
  '/guia-tpv',
  '/tpv-nube',
  '/cashdro',
  '/quioscos-autoservicio',
  '/inteligencia-artificial',
  '/hardware-tpv',
  '/canales-venta',
  '/medios-pago',
  '/verifactu',
  '/demo',
  '/precios',
  '/hosteleria',
  '/retail',
  '/alimentacion',
  '/belleza',
  '/cadenas-franquicias',
  '/tpv-barcelona',
  '/tpv-madrid',
  '/blog',
  '/legal',
  // Direcciones antiguas que se redirigen a su página definitiva.
  '/quienes-somos',
  '/about',
  '/about-us',
  '/complementos',
];

// Familias de rutas con parte variable (artículo del blog, apartado legal).
const LOCALIZED_PREFIXES = ['/blog/', '/legal/'];

const PREFIX_RE = /^\/(ca|en|fr)(?=\/|$)/;

export function prefixOf(lang) {
  return LANG_PREFIXES[lang] ?? '';
}

export function hasLangPrefix(pathname = '') {
  return PREFIX_RE.test(pathname);
}

export function langFromPath(pathname = '') {
  const match = PREFIX_RE.exec(pathname);
  return match ? match[1].toUpperCase() : 'ES';
}

/** Quita el prefijo de idioma: '/en/belleza' → '/belleza'. */
export function stripLang(pathname = '') {
  const match = PREFIX_RE.exec(pathname);
  if (!match) return pathname || '/';
  return pathname.slice(match[0].length) || '/';
}

function splitTail(path) {
  const index = path.search(/[?#]/);
  return index === -1 ? [path, ''] : [path.slice(0, index), path.slice(index)];
}

/**
 * Devuelve la misma dirección en el idioma indicado, conservando la parte
 * variable. Las direcciones externas o los enlaces internos por ancla se
 * devuelven tal cual.
 */
export function localizePath(path, lang) {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/') || path.startsWith('//')) return path;

  const [pathname, tail] = splitTail(path);
  const base = stripLang(pathname);
  const clean = base === '/' ? '' : base;

  return (prefixOf(lang) + clean || '/') + tail;
}

/** ¿Esta ruta tiene versión propia en cada idioma? */
export function isLocalizedPath(pathname = '') {
  const base = stripLang(pathname);
  if (LOCALIZED_PATHS.includes(base)) return true;
  return LOCALIZED_PREFIXES.some((prefix) => base.startsWith(prefix));
}